/**
 * Playing styles (P5 of docs/proposals/ai-personas-voice-and-sound.md): the
 * numeric meaning behind each style label. "A style is a small set of numeric
 * biases over each engine's own move choice. It must never make a persona
 * stronger or weaker than its strength band, and must never produce an illegal
 * or silly move." The engines take these numbers as plain data (see
 * `game-engine/src/style/rank.ts`); nothing in the engine layer imports this
 * package.
 *
 * All SIX spec styles exist as profiles even though the cast currently uses
 * five labels — the spec's open question about the style list (and whether a
 * persona is ever `aggressive`) is still with the operator, and shipping the
 * sixth profile now means answering it later needs no engine change.
 *
 * Calibration is the arbiter: the strength-budget tests (styled vs unstyled
 * self-play inside the 45–55% band) decide whether a profile ships as written
 * or gets flattened/dropped — spec §Playing styles: "If a style cannot hold its
 * strength it is dropped or loosened, not shipped."
 */
import { personaById } from './cast.js';

/** The spec's six style ids. `PlayingStyle` (the cast's five labels) ⊂ StyleId. */
export type StyleId = 'aggressive' | 'cautious' | 'tricky' | 'solid' | 'chaotic' | 'eager';

/**
 * Numeric biases, every axis 0..1:
 * - `aggression` prefers captures, checks, attacks, forward pushes
 * - `caution` prefers safety, solid structure, avoiding exposure and risk
 * - `complexity` prefers keeping tension and traps over simplifying
 * - `tempo` is think-time feel: quick and impulsive vs slow and deliberate
 *   (a movetime lever only — it never enters the move's style score)
 */
export interface PlayStyle {
  readonly id: StyleId;
  readonly aggression: number;
  readonly caution: number;
  readonly complexity: number;
  readonly tempo: number;
}

/**
 * The shipped profiles. Numbers are initial values pending calibration;
 * `solid` is deliberately near-neutral-but-careful because it is the default
 * persona's style (marisol) — the default robot's play moves as little as
 * possible while still being a style.
 */
export const STYLE_PROFILES: Readonly<Record<StyleId, PlayStyle>> = Object.freeze({
  aggressive: Object.freeze({ id: 'aggressive', aggression: 0.9, caution: 0.2, complexity: 0.5, tempo: 0.8 }),
  cautious: Object.freeze({ id: 'cautious', aggression: 0.2, caution: 0.9, complexity: 0.4, tempo: 0.35 }),
  tricky: Object.freeze({ id: 'tricky', aggression: 0.45, caution: 0.35, complexity: 0.9, tempo: 0.5 }),
  solid: Object.freeze({ id: 'solid', aggression: 0.4, caution: 0.75, complexity: 0.35, tempo: 0.45 }),
  chaotic: Object.freeze({ id: 'chaotic', aggression: 0.65, caution: 0.15, complexity: 0.8, tempo: 0.85 }),
  eager: Object.freeze({ id: 'eager', aggression: 0.7, caution: 0.4, complexity: 0.5, tempo: 0.95 }),
});

/** The numeric profile behind a persona's style label; undefined for unknown ids. */
export function styleForPersona(personaId: string): PlayStyle | undefined {
  const persona = personaById(personaId);
  return persona ? STYLE_PROFILES[persona.style] : undefined;
}
